import { useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAddCaseFormContext } from '../contexts/AddCaseFormContext';
import useDedicatedFileUpload from './useDedicatedFileUpload';
import useCaseFieldUpdate from './useCaseFieldUpdate';
import { buildPainSufferingPaths } from '../utils/caseFieldPaths';
import { prepFileMetaDataForDedicatedUpload } from '../utils/helper';
import { PAIN_POINT_CATEGORIES, UPDATE_CASE_ACTION } from '@demandproai/shared';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { updateCaseDataFile } from '../redux/actions/fileUpload';
import useCaseRecordDelete from './useCaseRecordDelete';

const usePainAndSufferingForm = () => {
    const caseDataFiles = useSelector(state => state.fileUpload.caseDataFiles);
    const { handleFileRemove } = useCaseRecordDelete();
    const { formik, selectedTiffIndex, getFieldProps } = useAddCaseFormContext();
    const { caseId } = useParams();
    const dispatch = useDispatch();
    const { isLoading, dedicatedImageUpload, startFileUpload, completeFileUpload } = useDedicatedFileUpload(caseId);
    const { updateCase } = useCaseFieldUpdate({ caseId });
   

    const textareaRef = useRef(null);
    // the checkbox list lives in shared so the virtual user agent selects from the same vocabulary a
    // human ticks - a value outside it checks no box
    const painPoints = useRef(PAIN_POINT_CATEGORIES);

    const formikValues = formik.values;
    const setFieldValue = formik.setFieldValue;
    const painSufferingPaths = buildPainSufferingPaths({ selectedTiffIndex });
    const currentPainAndSuffering = formikValues?.clients?.[selectedTiffIndex]?.painAndSuffering || {};
    const bodilyInjuriesImageFiles = currentPainAndSuffering?.bodilyInjuriesImageFiles || [];

    const handleAddBullet = useCallback((isNewLine = false) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const bulletText = formikValues?.clients?.[selectedTiffIndex]?.painAndSuffering?.descriptionText || '';
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const bullet = '\u2022 ';
        const insertText = (isNewLine ? '\n' : '') + bullet;
        const newText =
        bulletText.slice(0, start) +
        insertText +
        bulletText.slice(end);

        setFieldValue(`clients[${selectedTiffIndex}].painAndSuffering.descriptionText`, newText);
        requestAnimationFrame(() => {
            const pos = start + insertText.length;
            textarea.selectionStart = textarea.selectionEnd = pos;
            textarea.focus();
        });
    }, [formikValues, selectedTiffIndex, setFieldValue]);

    const handleKeyDown = useCallback((e) => {
        const bulletText = formikValues?.clients?.[selectedTiffIndex]?.painAndSuffering?.descriptionText || '';
        const bullet = '\u2022 ';
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddBullet(true);
        } else if (bulletText.length === 0) {
            setFieldValue(`clients[${selectedTiffIndex}].painAndSuffering.descriptionText`, bullet);
        }
    }, [formikValues, selectedTiffIndex, setFieldValue, handleAddBullet]);

    const bodilyInjuriesFilesOnChange = useCallback(async (files) => {
        const newFiles = files.filter(f => f.file && !f.s3UrlPath);

        setFieldValue(`clients[${selectedTiffIndex}].painAndSuffering.bodilyInjuriesImageFiles`, files);

        if (newFiles.length === 0) return;

        const prepFiles = prepFileMetaDataForDedicatedUpload(newFiles);
        const fileCount = prepFiles.length;

        startFileUpload(fileCount);
        const uploadedFiles = await dedicatedImageUpload(prepFiles, {
            fieldKey: `clients.${selectedTiffIndex}.painAndSuffering.bodilyInjuriesImageFiles`,
            action: 'add'
        });

        const mergedUploadedFiles = uploadedFiles.map((uploaded, index) => ({
            ...newFiles[index],
            ...uploaded,
            url: newFiles[index].url
        }));

        const currentBodilyInjuriesImageFiles = formikValues?.clients?.[selectedTiffIndex]?.painAndSuffering?.bodilyInjuriesImageFiles || [];

        let combinedArr = [
            ...(currentBodilyInjuriesImageFiles || []).filter(f => !newFiles.includes(f)),
            ...mergedUploadedFiles,
        ];

        combinedArr = [...new Map(combinedArr.map(item => [item.s3UrlPath || item.url || item.file?.name, item])).values()];

        setFieldValue(`clients[${selectedTiffIndex}].painAndSuffering.bodilyInjuriesImageFiles`, combinedArr);
        completeFileUpload(fileCount);
    }, [formikValues, selectedTiffIndex, setFieldValue, startFileUpload, dedicatedImageUpload, completeFileUpload]);

    const bodilyInjuriesIsSelectedChangeHandler = useCallback(async (s3UrlPath, isSelected) => {
        const bodilyInjuriesImageFiles = formikValues?.clients?.[selectedTiffIndex]?.painAndSuffering?.bodilyInjuriesImageFiles || [];
        const fileIndex = bodilyInjuriesImageFiles?.findIndex(file => file.s3UrlPath === s3UrlPath);
        if (fileIndex !== -1) {
            if(caseDataFiles.get(s3UrlPath)){
                dispatch(updateCaseDataFile({s3UrlPath, isSelected:isSelected}));
            }else{
                await updateCase({ data: { [painSufferingPaths.bodilyInjuries.isSelected(fileIndex)]: isSelected }, action: UPDATE_CASE_ACTION.UPDATE });
            }
        }
    }, [formikValues, selectedTiffIndex, painSufferingPaths, updateCase, caseDataFiles, dispatch]);

    const bodilyInjuriesRemoveHandler = useCallback(async (s3UrlPath) => {
        handleFileRemove({
            formik,
            fileToRemove:{s3UrlPath},
            removeData: { [painSufferingPaths.bodilyInjuries.files]: { s3UrlPath } }
        });
    }, [formik, handleFileRemove, painSufferingPaths]);

    const handlePastTotalToggle = useCallback((e) => {
        const isTotal = e.target.checked;
        setFieldValue(`clients[${selectedTiffIndex}].painAndSuffering.isPastTotalAmount`, isTotal);
        updateCase({ data: { [painSufferingPaths.isPastTotalAmount]: isTotal } });
    }, [selectedTiffIndex, setFieldValue, painSufferingPaths, updateCase]);

    const handleFutureTotalToggle = useCallback((e) => {
        const isTotal = e.target.checked;
        setFieldValue(`clients[${selectedTiffIndex}].painAndSuffering.isFutureTotalAmount`, isTotal);
        updateCase({ data: { [painSufferingPaths.isFutureTotalAmount]: isTotal } });
    }, [selectedTiffIndex, setFieldValue, painSufferingPaths, updateCase]);

    const handlePainPointToggle = useCallback((point) => {
        const selectedPoints = formikValues?.clients?.[selectedTiffIndex]?.painAndSuffering?.selectedPainPoints || [];
        const newPoints = selectedPoints.includes(point)
            ? selectedPoints.filter(item => item !== point)
            : [...selectedPoints, point];

        setFieldValue(`clients[${selectedTiffIndex}].painAndSuffering.selectedPainPoints`, newPoints);
        updateCase({ data: { [painSufferingPaths.selectedPainPoints]: newPoints } });
    }, [formikValues, selectedTiffIndex, setFieldValue, painSufferingPaths, updateCase]);

    return {
        selectedTiffIndex,
        painPoints: painPoints.current,
        textareaRef,
        isLoading,
        painSufferingPaths,
        getFieldProps,
        currentPainAndSuffering,
        bodilyInjuriesImageFiles,
        handleAddBullet,
        handleKeyDown,
        bodilyInjuriesFilesOnChange,
        bodilyInjuriesIsSelectedChangeHandler,
        bodilyInjuriesRemoveHandler,
        handlePainPointToggle,
        handlePastTotalToggle,
        handleFutureTotalToggle,
    };
};

export default usePainAndSufferingForm;
